import { useEffect, useRef, useState } from "react";
import { RegexFieldMatcher } from "../../../models/matchers/RegexFieldMatcher";
import { RegexValidatorResponse } from "../../../models/regex/RegexValidator";
import { calculateInputLeft, useTooltip } from "../../hooks/useTooltip";
import Icon from "../../Icon";
import { InputGroup } from "../../inputGroup/InputGroup";
import { RegexError } from "../../regex/RegexError";
import { RegexInput } from "../../regex/RegexInput";
import { RegexFieldMatcherEditorProps } from "./RegexFieldMatcherEditor";

import "../../../styles/regex/suggestions.css";
import { Button } from "../../buttons/Button";
import { DlfMatcherLabel } from "../../inputGroup/DlfMatcherLabel";
import {
  RegexSuggestionList,
  SuggestionType,
} from "../../regex/RegexSuggestions";
import { AdvancedTooltip } from "../../tooltips/AdvancedTooltip";
import { ExpandingTooltip } from "../../tooltips/ExpandingTooltip";

export {
  RegexFieldMatcherEditorWithSuggestions,
  Props as RegexFieldMatcherEditorWithSuggestionsProps,
};

type Props = {
  matcher: RegexFieldMatcher;
  update: (matcher: RegexFieldMatcher) => void;
  reusable?: boolean;
  suggestions?: SuggestionType[];
  suggestionsTooltip?: ExpandingTooltip;
  suggestionsTooltipShort?: boolean;
  suggestionsTooltipOverflow?: boolean;
} & RegexFieldMatcherEditorProps;

const RegexFieldMatcherEditorWithSuggestions = (props: Props) => {
  // triggerRef because react-laang doesn't update when our buttons rerendered
  const triggerRef = useRef<HTMLButtonElement>(null);
  // container to attach suggestion tooltips too as tooltip cannot be attached to <input>
  const groupRef = useRef<HTMLSpanElement>(null);
  // calculate size and position of tooltip
  const inputRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<RegexValidatorResponse | undefined>();

  const { isOpen, layerProps, renderLayer, setOpen, triggerProps } = useTooltip(
    groupRef,
    undefined,
    undefined,
    undefined,
    "bottom-start",
    ["bottom-start"],
  );

  const hasSuggestions = props.suggestions && props.suggestions.length;

  // update react-laang trigger ref as needed
  useEffect(() => {
    triggerProps?.ref(triggerRef.current);
  }, [triggerRef, triggerRef.current]);

  return (
    <div className={"fieldMatcherEditor regexDlfEditor"}>
      <InputGroup ref={groupRef} button={(hasSuggestions ?? 0) > 0}>
        <DlfMatcherLabel label={"Regex"} {...props} />

        <RegexInput
          ref={inputRef}
          initialValue={props.matcher.regex}
          parentRef={groupRef}
          updated={(result) => {
            setError(result);
            if (result.valid) {
              props.matcher.regex = result.regex;
              props.update(props.matcher);
            }
          }}
        />

        {(hasSuggestions ?? 0) > 0 && (
          <AdvancedTooltip
            tooltipText={props.suggestionsTooltip}
            overflow={props.suggestionsTooltipOverflow}
            short={props.suggestionsTooltipShort}
          >
            {(tProps) => (
              <Button
                {...tProps}
                ref={(r) => {
                  triggerRef.current = r;
                  tProps.ref(r);
                }}
                className={"suggestions"}
                onClick={(r) => setOpen(!isOpen)}
              >
                <Icon altText={""} icon={"chevron-down"} />
              </Button>
            )}
          </AdvancedTooltip>
        )}
        {isOpen &&
          renderLayer(
            <RegexSuggestionList
              layerProps={layerProps}
              suggestions={props.suggestions}
              style={{
                ...layerProps.style,
                display: undefined,
                width: inputRef.current!.clientWidth - 2,
                left: calculateInputLeft(inputRef.current!),
                // negative 1 px for nice border overlap
                marginTop: "-1px",
                height: "auto",
              }}
              onClick={(regex) => {
                setOpen(false);
                props.matcher.regex = regex;
                props.update(props.matcher);
              }}
            />,
          )}
        {props.button && props.button}
      </InputGroup>
      <RegexError error={error} />
    </div>
  );
};
